import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ChevronDown, Search, X} from 'lucide-react';
import {useDebounce} from '../hooks';
import {Box} from "@mui/material";

const defaultPagination = (page, pageSize) => ({
    page,
    page_size: pageSize,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
});

const unwrapPayload = (response) => {
    const root = response?.data ?? response ?? {};
    return root?.valor ?? root;
};

const getItems = (payload) => {
    if (Array.isArray(payload)) return payload;

    const keys = ['items', 'data', 'content', 'results', 'registros', 'conteudo'];
    for (const key of keys) {
        if (Array.isArray(payload?.[key])) return payload[key];
    }

    return [];
};

const normalizeResponse = (response, fallbackPage, fallbackPageSize, pageBase) => {
    const payload = unwrapPayload(response);
    const items = getItems(payload);
    const paginationSource = payload?.pagination ?? payload?.meta ?? payload ?? {};
    const total = Number(
        paginationSource.total
        ?? paginationSource.totalItems
        ?? paginationSource.total_items
        ?? paginationSource.total_elements
        ?? payload?.total
        ?? payload?.totalElements
        ?? payload?.count
        ?? items.length
    );
    const page = Number(
        paginationSource.page
        ?? paginationSource.current_page
        ?? payload?.page
        ?? fallbackPage
    );
    const pageSize = Number(
        paginationSource.page_size
        ?? paginationSource.pageSize
        ?? paginationSource.size
        ?? paginationSource.limit
        ?? payload?.page_size
        ?? payload?.pageSize
        ?? fallbackPageSize
    );
    const totalPages = Number(
        paginationSource.total_pages
        ?? paginationSource.totalPages
        ?? paginationSource.pages
        ?? Math.max(1, Math.ceil(total / Math.max(pageSize, 1)))
    );

    return {
        items,
        pagination: {
            page,
            page_size: pageSize,
            total,
            total_pages: totalPages,
            has_next: Boolean(
                paginationSource.has_next
                ?? paginationSource.hasNext
                ?? (pageBase === 1 ? page < totalPages : page + 1 < totalPages)
            ),
            has_previous: Boolean(
                paginationSource.has_previous
                ?? paginationSource.hasPrevious
                ?? (pageBase === 1 ? page > 1 : page > 0)
            ),
        },
    };
};

const styles = {
    root: {
        position: 'relative',
        width: '100%',
    },
    label: {
        display: 'block',
        marginBottom: 6,
        color: '#374151',
        fontSize: 13,
        fontWeight: 500,
    },
    required: {
        color: '#ef4444',
    },
    trigger: {
        width: '100%',
        minHeight: 43,
        padding: '10px 14px',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        background: '#fff',
        color: '#111827',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: 14,
        transition: 'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',
    },
    disabledTrigger: {
        background: '#f3f4f6',
        cursor: 'not-allowed',
        color: '#6b7280',
    },
    openTrigger: {
        borderColor: '#0f766e',
        boxShadow: '0 0 0 2px rgba(15, 118, 110, 0.18)',
    },
    triggerContent: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        gap: 8,
    },
    triggerText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    placeholder: {
        color: '#6b7280',
    },
    triggerActions: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        gap: 4,
    },
    clearButton: {
        width: 26,
        height: 26,
        padding: 0,
        border: 0,
        borderRadius: 6,
        background: 'transparent',
        color: '#9ca3af',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    dropdown: {
        position: 'absolute',
        zIndex: 50,
        top: 'calc(100% + 8px)',
        left: 0,
        width: '100%',
        overflow: 'hidden',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        background: '#fff',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.16)',
    },
    searchBox: {
        padding: 12,
        borderBottom: '1px solid #e5e7eb',
        background: '#f9fafb',
    },
    searchWrapper: {
        position: 'relative',
    },
    searchIcon: {
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#9ca3af',
        pointerEvents: 'none',
    },
    input: {
        width: '100%',
        height: 38,
        padding: '8px 12px 8px 38px',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        outline: 'none',
        color: '#111827',
        fontSize: 14,
        boxSizing: 'border-box',
    },
    list: {
        maxHeight: 240,
        overflowY: 'auto',
    },
    option: {
        width: '100%',
        padding: '10px 14px',
        border: 0,
        background: '#fff',
        color: '#111827',
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    selectedOption: {
        background: '#f0fdfa',
        color: '#0f766e',
    },
    optionLabel: {
        fontSize: 14,
        lineHeight: 1.3,
    },
    optionDescription: {
        marginTop: 3,
        fontSize: 12,
        color: '#6b7280',
        lineHeight: 1.25,
    },
    selectedDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: '#0f766e',
        flexShrink: 0,
    },
    state: {
        padding: 16,
        color: '#6b7280',
        textAlign: 'center',
        fontSize: 14,
    },
    spinner: {
        width: 20,
        height: 20,
        margin: '0 auto 8px',
        border: '2px solid #0f766e',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'remote-select-spin 800ms linear infinite',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: 8,
        borderTop: '1px solid #e5e7eb',
        background: '#f9fafb',
        color: '#6b7280',
        fontSize: 12,
    },
    pageButton: {
        border: '1px solid #d1d5db',
        borderRadius: 6,
        background: '#fff',
        color: '#374151',
        padding: '5px 8px',
        fontSize: 12,
        cursor: 'pointer',
    },
    helper: {
        marginTop: 4,
        color: '#6b7280',
        fontSize: 12,
    },
    error: {
        marginTop: 4,
        color: '#dc2626',
        fontSize: 12,
    },
};

export default function RemoteSelect({
    label,
    value,
    onChange,
    fetchOptions,
    getOptionValue = (option) => option?.id,
    getOptionLabel = (option) => option?.nome ?? option?.name ?? option?.label ?? '',
    getOptionDescription,
    icon: Icon,
    placeholder = 'Selecione uma opção',
    searchPlaceholder = 'Buscar...',
    emptyMessage = 'Nenhuma opção disponível',
    noResultsMessage = 'Nenhum resultado encontrado',
    loadingMessage = 'Carregando...',
    readOnly = false,
    disabled = false,
    clearable = true,
    pageSize = 10,
    initialPage = 1,
    pageBase = 1,
    debounceDelay = 400,
    loadOnMount = true,
    helperText,
    error,
    required = false,
    className,
    serviceParams,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [pagination, setPagination] = useState(defaultPagination(initialPage, pageSize));
    const [page, setPage] = useState(initialPage);
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [requestError, setRequestError] = useState('');
    const debouncedSearch = useDebounce(searchTerm, debounceDelay);
    const dropdownRef = useRef(null);
    const requestIdRef = useRef(0);
    const serviceParamsRef = useRef(serviceParams);
    const previousSearchRef = useRef('');

    useEffect(() => {
        serviceParamsRef.current = serviceParams;
    }, [serviceParams]);

    const selectedValue = value ? getOptionValue(value) : null;
    const displayText = value ? getOptionLabel(value) : placeholder;
    const isDisabled = readOnly || disabled;

    const loadOptions = useCallback(async (query, nextPage) => {
        if (!fetchOptions) return;

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setLoading(true);
        setRequestError('');

        try {
            const response = await fetchOptions({
                query,
                page: nextPage,
                pageSize,
                ...serviceParamsRef.current,
            });
            if (requestId !== requestIdRef.current) return;

            const payload = normalizeResponse(response, nextPage, pageSize, pageBase);
            setOptions(payload.items);
            setPagination(payload.pagination);
            setHasLoaded(true);
        } catch (loadError) {
            if (requestId !== requestIdRef.current) return;
            setOptions([]);
            setPagination(defaultPagination(nextPage, pageSize));
            setRequestError(loadError?.message || 'Erro ao buscar opções');
            setHasLoaded(true);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [fetchOptions, pageBase, pageSize]);

    useEffect(() => {
        if (!loadOnMount) return;
        loadOptions('', initialPage);
    }, [initialPage, loadOnMount, loadOptions]);

    useEffect(() => {
        if (!isOpen || hasLoaded || loadOnMount) return;
        loadOptions('', initialPage);
    }, [hasLoaded, initialPage, isOpen, loadOnMount, loadOptions]);

    useEffect(() => {
        if (!hasLoaded) return;
        if (previousSearchRef.current === debouncedSearch) return;
        previousSearchRef.current = debouncedSearch;
        setPage(initialPage);
        loadOptions(debouncedSearch, initialPage);
    }, [debouncedSearch, hasLoaded, initialPage, loadOptions]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        onChange?.(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (event) => {
        event.stopPropagation();
        onChange?.(null);
        setSearchTerm('');
        previousSearchRef.current = '';
        setPage(initialPage);
        loadOptions('', initialPage);
    };

    const changePage = (nextPage) => {
        setPage(nextPage);
        loadOptions(debouncedSearch, nextPage);
    };

    const footerText = useMemo(() => {
        const total = Number(pagination.total || options.length || 0);
        const totalPages = Number(pagination.total_pages || 1);
        const currentPage = Number(pagination.page ?? page);
        return `Mostrando ${options.length} de ${total} resultado(s) - Página ${currentPage} de ${totalPages}`;
    }, [options.length, page, pagination]);

    return (
        <div className={className} style={styles.root} ref={dropdownRef}>
            <style>{'@keyframes remote-select-spin { to { transform: rotate(360deg); } }'}</style>
            {label && (
                <label style={styles.label}>
                    {label}{required ? <Box component="span" sx={styles.required}> *</Box> : ''}
                </label>
            )}

            <button
                type="button"
                onClick={() => !isDisabled && setIsOpen((current) => !current)}
                disabled={isDisabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                style={{
                    ...styles.trigger,
                    ...(isDisabled ? styles.disabledTrigger : null),
                    ...(isOpen ? styles.openTrigger : null),
                    ...(error ? {borderColor: '#dc2626'} : null),
                }}
            >
                <span style={styles.triggerContent}>
                    {Icon && <Icon size={16} color="#9ca3af" />}
                    <span style={{...styles.triggerText, ...(!value ? styles.placeholder : null)}}>{displayText}</span>
                </span>
                <span style={styles.triggerActions}>
                    {value && clearable && !isDisabled && (
                        <span
                            role="button"
                            tabIndex={0}
                            aria-label="Limpar seleção"
                            onClick={handleClear}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') handleClear(event);
                            }}
                            style={styles.clearButton}
                        >
                            <X size={16} />
                        </span>
                    )}
                    {!isDisabled && <ChevronDown size={16} color="#9ca3af" />}
                </span>
            </button>

            {isOpen && !isDisabled && (
                <div style={styles.dropdown}>
                    <div style={styles.searchBox}>
                        <div style={styles.searchWrapper}>
                            <Search size={16} style={styles.searchIcon} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder={searchPlaceholder}
                                style={styles.input}
                                autoFocus
                            />
                        </div>
                    </div>

                    <div style={styles.list} role="listbox">
                        {loading ? (
                            <div style={styles.state}>
                                <div style={styles.spinner} />
                                <div>{loadingMessage}</div>
                            </div>
                        ) : requestError ? (
                            <div style={styles.state}>{requestError}</div>
                        ) : options.length > 0 ? (
                            options.map((option) => {
                                const optionValue = getOptionValue(option);
                                const selected = selectedValue === optionValue;
                                const description = getOptionDescription?.(option);

                                return (
                                    <button
                                        key={optionValue}
                                        type="button"
                                        role="option"
                                        aria-selected={selected}
                                        onClick={() => handleSelect(option)}
                                        style={{
                                            ...styles.option,
                                            ...(selected ? styles.selectedOption : null),
                                        }}
                                        onMouseEnter={(event) => {
                                            if (!selected) event.currentTarget.style.background = '#f0fdfa';
                                        }}
                                        onMouseLeave={(event) => {
                                            if (!selected) event.currentTarget.style.background = '#fff';
                                        }}
                                    >
                                        <span>
                                            <span style={styles.optionLabel}>{getOptionLabel(option)}</span>
                                            {description && <span style={styles.optionDescription}>{description}</span>}
                                        </span>
                                        {selected && <span style={styles.selectedDot} />}
                                    </button>
                                );
                            })
                        ) : (
                            <div style={styles.state}>
                                {searchTerm ? noResultsMessage : emptyMessage}
                            </div>
                        )}
                    </div>

                    <div style={styles.footer}>
                        <button
                            type="button"
                            onClick={() => changePage(Math.max(pageBase === 1 ? 1 : 0, Number(pagination.page ?? page) - 1))}
                            disabled={!pagination.has_previous || loading}
                            style={{
                                ...styles.pageButton,
                                opacity: !pagination.has_previous || loading ? 0.45 : 1,
                                cursor: !pagination.has_previous || loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            Anterior
                        </button>
                        <span>{footerText}</span>
                        <button
                            type="button"
                            onClick={() => changePage(Number(pagination.page ?? page) + 1)}
                            disabled={!pagination.has_next || loading}
                            style={{
                                ...styles.pageButton,
                                opacity: !pagination.has_next || loading ? 0.45 : 1,
                                cursor: !pagination.has_next || loading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            Próxima
                        </button>
                    </div>
                </div>
            )}

            {error ? <div style={styles.error}>{error}</div> : helperText ? <div style={styles.helper}>{helperText}</div> : null}
            {value && readOnly && getOptionDescription?.(value) && (
                <div style={styles.helper}>{getOptionDescription(value)}</div>
            )}
        </div>
    );
}
